import React, { useEffect, useState, useContext, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import BookingHeader from "./clientPortal/BookingHeader";
import "react-datepicker/dist/react-datepicker.css";
import Select from "react-select";
import { EventDataContext } from "./EventDataContext";
import { useDispatch } from "react-redux";
import { loginSuccess, loginFailure } from "../authSlice";
import axios from "axios";
import { FaEye, FaEyeSlash } from "react-icons/fa"; // Import icons

const BookABoothLink = () => {
  const [packages, setPackages] = useState([]);
  const [addons, setAddons] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [selectedAddonIds, setSelectedAddonIds] = useState([]);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [hoveredAddonId, setHoveredAddonId] = React.useState(null);
  const [loadingPackages, setLoadingPackages] = useState(true);
  const [loadingAddons, setLoadingAddons] = useState(true);
  const [error, setError] = useState(null);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const businessId = searchParams.get("businessId");
  const { EventData, setEventData } = useContext(EventDataContext);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [userData, setUserData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    name: "",
    phone:"",
  });
  const passwordInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [userExists, setUserExists] = useState(null);

  // 1. Add a new state at the top of your component
const [isSubscribed, setIsSubscribed] = useState(true); // Default to true to avoid flicker



  // State to manage password visibility for each field
  const [passwordVisibility, setPasswordVisibility] = useState({
    login: false,
    new: false,
    confirm: false,
  });

  useEffect(() => {
    if (!businessId) return;

    // Save to session storage
    sessionStorage.setItem("businessId", businessId);

    const initializeBookingPage = async () => {
      try {
        setLoadingPackages(true);
        setLoadingAddons(true);

        // 1. Check Subscription first
        const subResponse = await fetch(`${process.env.REACT_APP_API_URL}/business/check-subscription?businessId=${businessId}`);
        const subData = await subResponse.json();

        // If inactive (meaning it's PAST_DUE or CANCELLED), block the page
        if (!subData.success || subData.status !== 'active') {
          setIsSubscribed(false);
          alert("This booking link is currently inactive. Please contact the photo booth business directly for assistance.");
          return; // STOP execution here. Do not fetch packages.
        }

        // 2. Fetch Packages (Only happens if subscribed!)
        const pkgRes = await fetch(`${process.env.REACT_APP_API_URL}/business/get-packages?businessId=${businessId}`);
        const pkgData = await pkgRes.json();
        const activePkgs = (Array.isArray(pkgData) ? pkgData : pkgData.data || [])
          .filter(item => item.status !== 'ARCHIVED')
          .sort((a, b) => (a.position || 0) - (b.position || 0));
        setPackages(activePkgs);

        // 3. Fetch Addons
        const addonRes = await fetch(`${process.env.REACT_APP_API_URL}/business/get-addon?businessId=${businessId}`);
        const addonData = await addonRes.json();
        const activeAddons = (Array.isArray(addonData) ? addonData : addonData.data || [])
          .filter(item => item.status !== 'ARCHIVED')
          .sort((a, b) => (a.position || 0) - (b.position || 0));
        setAddons(activeAddons);

      } catch (err) {
        setError(err.message);
      } finally {
        setLoadingPackages(false);
        setLoadingAddons(false);
      }
    };

    initializeBookingPage();
  }, [businessId]);

  // Keep your password focus effect
  useEffect(() => {
    if (userExists === true) {
      passwordInputRef.current?.focus();
    }
  }, [userExists]);


  useEffect(() => {
    if (userExists === true) {
      passwordInputRef.current?.focus();
    }
  }, [userExists]);

  // Handler to toggle password visibility
  const togglePasswordVisibility = (field) => {
    setPasswordVisibility((prevState) => ({
      ...prevState,
      [field]: !prevState[field],
    }));
  };

  

  // 2. Early Return if business is not subscribed
  if (!isSubscribed) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 20px', fontFamily: 'poppins' }}>
        <h2 style={{ color: '#1C1154' }}>Link Inactive</h2>
        <p style={{ color: '#625C73' }}>
          This booking page is currently unavailable. 
          Please contact the business owner directly to schedule your event.
        </p>
      </div>
    );
  }

  const validateForm = () => {
    const errors = {};
    if (!selectedPackage) {
      errors.package = "Please select a package.";
    }
    if (!EventData.eventName.trim()) {
      errors.eventName = "Event name is required.";
    }
    if (!EventData.eventType) {
      errors.eventType = "Please select an event type.";
    }
    if (!EventData.eventDate) {
      errors.eventDate = "Please choose a date.";
    }
    if (!EventData.startTime) {
      errors.startTime = "Start time is required.";
    }
    if (!EventData.endTime) {
      errors.endTime = "End time is required.";
    }
    if (!EventData.location.trim()) {
      errors.location = "Location is required.";
    }

    // --- New User Data Validation (Sign Up Flow) ---
  if (userExists === false) {
    if (!userData.name.trim()) {
      errors.name = "Full name is required.";
    }
    
    if (!userData.phone.trim()) {
      errors.phone = "Phone number is required.";
    } 

    if (!userData.password) {
      errors.password = "Password is required.";
    } else if (userData.password.length < 6) {
      errors.password = "Password must be at least 6 characters.";
    }

    if (userData.password !== userData.confirmPassword) {
      errors.confirmPassword = "Passwords do not match.";
    }
  }

    

    if (EventData.eventDate && EventData.startTime && EventData.endTime) {
      const startDateTime = new Date(
        `${EventData.eventDate}T${EventData.startTime}`
      );
      const endDateTime = new Date(
        `${EventData.eventDate}T${EventData.endTime}`
      );
      if (startDateTime >= endDateTime) {
        errors.time = "End time must be later than the start time.";
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleUserFormInputChange = (e) => {
    const { name, value } = e.target;
    setUserData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEventData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleCheckExistingUser = async () => {
    if (!validateForm()) return;

    try {
      setLoadingPackages(true);
      setError(null);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/client/client-exists`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: userData.email }),
        }
      );
      if (!response.ok) throw new Error("Failed to check if client exists");
      const data = await response.json();
      setUserExists(data.data.exists);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingPackages(false);
      // --- ADD THIS ---
      // Use setTimeout to wait for React to re-render before scrolling
      setTimeout(() => {
        window.scroll({
          top: document.body.scrollHeight, // Scrolls to the very bottom
          behavior: "smooth"               // Adds a smooth animation
        });
      }, 0);
      // --- END OF CHANGE ---
    }
    
  };


  const handleCreateAccountAndEvent = async () => {
    if (!validateForm()) return;

    // ... (your form validation logic) ...

    const userPayload = {
      name: userData.name,
      email: userData.email,
      password: userData.password,
      phone: userData.phone,
      businessId: businessId, //first business that registers the user. 
      profilePic: "https://picsum.photos/200/300",
      coverpic: "https://source.unsplash.com/1200x400/?nature,landscape",
    };

    try {
      setLoading(true);
      setError(null);
      
      // 1. Create the user
      const onBoardResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/client-user/onboard`,
        userPayload,
        { withCredentials: true }
      );

      // // 2. Immediately log the user in
      // const loginResponse = await axios.post(
      //   `${process.env.REACT_APP_API_URL}/client-user/login`,
      //   { email: userData.email, password: userData.password }, 
      //   { withCredentials: true }
      // );

      // // --- START OF FIX ---
      // // Get the response body directly
      // const loginData = loginResponse.data.data; // This is: { userId, userName, verified, ... }
        const onBoardData  = onBoardResponse.data.data;
        console.log(onBoardData);
      // Manually build the 'userWithRole' object
      // const userWithRole = {
      //   _id: loginData.userId,      // Map 'userId' to '_id'
      //   name: loginData.userName,   // Map 'userName' to 'name'
      //   verified: loginData.verified,
      //   role: 'Client'
      // };
      // --- END OF FIX ---
      
      // 4. Dispatch the correctly structured user
      // dispatch(loginSuccess(userWithRole));

      // 5. Create the event with the correct ID and name
      await createEvent(onBoardData.id, userPayload.name);
      
      // 6. Navigate to verification (this will now work)
      navigate("/check-verification");

    } catch (err) {
      console.error("Error occurred:", err);
      dispatch(loginFailure(err.message || "Failed to create account"));
      setError("Failed to create account or event. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  
  const createEvent = async (userId, clientName) => {
    // This function is now simpler as it receives all the user info it needs.
    console.log(userId);
    console.log(clientName);
    const eventPayload = {
      userId,
      businessId,
      packageId: selectedPackage?._id,
      eventName: EventData.eventName,
      addonIds: selectedAddonIds,
      status: "pending",
      eventDate: EventData.eventDate,
      startTime: new Date(`${EventData.eventDate}T${EventData.startTime}`).toISOString(),
      endTime: new Date(`${EventData.eventDate}T${EventData.endTime}`).toISOString(),
      address: EventData.location,
      clientName, // Use the name passed from the login response
      payment: "pending",
      eventType: EventData.eventType,
      eventRef: EventData.referralSource,
      details: EventData.details,
      type: "automatic",
    };

    try {
      const eventResponse = await axios.post(`${process.env.REACT_APP_API_URL}/create-event`, eventPayload, { withCredentials: true });
      return eventResponse.data.data;
    } catch (err) {
      console.error("Error occurred during event creation:", err);
      setError("Failed to create the event. Please try again.");
    }
  };

  const handleLogin = async () => {
    if (!validateForm()) return;

    try {
      const loginResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/client-user/login`,
        { email: userData?.email, password: userData?.password },
        { withCredentials: true }
      );

      // --- START OF FIX ---
      // Instead of getting loginResponse.data.data, we get the response body directly
      const loginData = loginResponse.data.data; // This is: { userId, userName, verified, ... }

      // Manually build the 'userWithRole' object in the format the rest of the code expects
      const userWithRole = {
        _id: loginData.userId,      // Map 'userId' to '_id'
        name: loginData.userName,   // Map 'userName' to 'name'
        verified: loginData.verified,
        role: 'Client'
      };
      // --- END OF FIX ---

      // Dispatch the correctly structured user object
      dispatch(loginSuccess(userWithRole));

      // Now, the rest of your logic will work perfectly
      if (!userWithRole.verified) {
        navigate("/verify-email", { state: { userId: userWithRole._id } });
        return;
      }
      
      const eventsResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/client/events/${businessId}`,
        { withCredentials: true }
      );
      const events = eventsResponse.data.data;

      // This will now pass the correct ID and name
      const newEvent = await createEvent(userWithRole._id, userWithRole.name);

      if (!newEvent) return;
      
      const unpaidEvent = events.find(event => event.payment === "pending");
      if (unpaidEvent) {
          try {
            await axios.delete(`${process.env.REACT_APP_API_URL}/deleteEvent/${unpaidEvent._id}`, { withCredentials: true });
          } catch (error) {
            console.error("Error deleting unpaid event:", error);
          }
      }
      
      navigate("/booth2", { state: { event: newEvent.event } });

    } catch (error) {
      console.error("Login or event fetching error:", error);
      dispatch(loginFailure(error.message));
    }
  };

  const options = [
    { value: "wedding", label: "Wedding" },
    { value: "corporate", label: "Corporate" },
    { value: "birthday", label: "Birthday" },
    { value: "others", label: "Others" },
  ];

  const referalOptions = [
    { value: "friends", label: "Friends or Family" },
    { value: "website", label: "Website" },
    { value: "socialMedia", label: "Social Media" },
    { value: "other", label: "Other" },
  ];

  const customStyles = {
    control: (base, state) => ({
      ...base,
      border: state.isFocused ? "1px solid #260F99" : "1px solid #ccc",
      boxShadow: state.isFocused ? "0 0 0 1px #260F99" : "none",
      "&:hover": { borderColor: state.isFocused ? "#260F99" : "#B3B3B3" },
      height: "50px", fontSize: "15px",
    }),
    indicatorSeparator: () => null,
  };

  const today = new Date().toISOString().split("T")[0];

  

  const handlePackageSelect = (pkg) => setSelectedPackage(pkg);

  const handleAddonSelect = (addon) => {
    setSelectedAddons(prev => prev.some(a => a._id === addon._id) ? prev.filter(a => a._id !== addon._id) : [...prev, addon]);
    setSelectedAddonIds(prev => prev.includes(addon._id) ? prev.filter(id => id !== addon._id) : [...prev, addon._id]);
  };

  if (loadingPackages || loadingAddons) return <div>Loading...</div>;
  if (error) return <div>Error: {error}. Please try again later.</div>;

  return (
    <div>
      <BookingHeader />
      <div style={styles.bookAbooth}>
        <div style={styles.panel}>
          <div style={styles.invoiceContent}>
            <h2 style={styles.heading}>Choose Package</h2>
            {formErrors.package && <p style={styles.errorText}>{formErrors.package}</p>}
            {packages.map(pkg => (
              <div
                key={pkg._id}
                style={{ ...styles.package, border: selectedPackage?._id === pkg._id ? "2px solid #260F99" : "1px solid #ddd" }}
                onClick={() => handlePackageSelect(pkg)}
              >
                <div style={styles.packageHeader}>
                  <h3 style={styles.packageName}>{pkg.name}</h3>
                  <div>${pkg.cost}</div>
                </div>
                <div style={styles.packageDescription}>
                  {pkg.description.split("\n").map((feature, i) => <p key={i}>{feature}</p>)}
                </div>
              </div>
            ))}

            <h2 style={{ ...styles.heading, marginTop: "30px" }}>Add Ons</h2>
            {addons.map(addon => (
              <div
                key={addon._id}
                style={{ ...styles.addon, border: selectedAddons.some(a => a._id === addon._id) ? "2px solid #260F99" : "1px solid #ddd" }}
                onClick={() => handleAddonSelect(addon)}
              >
                <div style={styles.addonContent}>
                  <h3 style={styles.addonName}>{addon.name}</h3>
                  {/* CHANGED: Split the description by newline to render as a list */}
                  <div style={styles.addonDescription}>
                    {(addon.description || "").split("\n").map((feature, i) => (
                      <p key={i} style={{ margin: "3px 0" }}>{feature}</p>
                    ))}
                  </div>
                </div>
                <div style={{ marginLeft: "auto", fontWeight: "bold" }}>${addon.cost}</div>
              </div>
            ))}
          </div>

          <div style={styles.eventForm}>
            <h2 style={styles.formHeading}>Schedule Your Event</h2>

            <h4 style={styles.label}>Name of your event</h4>
            <input placeholder="Event Name" style={styles.input} value={EventData.eventName} name="eventName" onChange={handleInputChange} />
            {formErrors.eventName && <p style={styles.errorText}>{formErrors.eventName}</p>}

            <h4 style={styles.label}>What is your event?</h4>
            <div style={{ width: "102%" }}>
              <Select options={options} styles={customStyles} placeholder="Select Event Type" name="eventType" onChange={opt => setEventData(prev => ({...prev, eventType: opt.value}))} value={options.find(o => o.value === EventData.eventType)} />
            </div>
            {formErrors.eventType && <p style={styles.errorText}>{formErrors.eventType}</p>}

            <h4 style={styles.label}>How did you hear about us?</h4>
            <div style={{ width: "102%" }}>
              <Select options={referalOptions} styles={customStyles} placeholder="Select Referral Source" name="referralSource" onChange={opt => setEventData(prev => ({...prev, referralSource: opt.value}))} value={referalOptions.find(o => o.value === EventData.referralSource)} />
            </div>

            <h4 style={styles.label}>Choose a Date</h4>
            <input style={styles.input} type="date" name="eventDate" value={EventData.eventDate} min={today} onChange={handleInputChange} />
            {formErrors.eventDate && <p style={styles.errorText}>{formErrors.eventDate}</p>}

            <h4 style={styles.label}>What time does your event start and end?</h4>
            <div style={{ display: "flex", width: "102%", gap: "10px" }}>
              <div style={{ flex: 1 }}>
                <input style={{ ...styles.input, width: "100%" }} type="time" name="startTime" value={EventData.startTime} onChange={handleInputChange} />
                {formErrors.startTime && <p style={styles.errorText}>{formErrors.startTime}</p>}
              </div>
              <div style={{ marginTop: "15px" }}>to</div>
              <div style={{ flex: 1 }}>
                <input style={{ ...styles.input, width: "100%" }} type="time" name="endTime" value={EventData.endTime} onChange={handleInputChange} />
                {formErrors.endTime && <p style={styles.errorText}>{formErrors.endTime}</p>}
              </div>
            </div>
            {formErrors.time && <p style={styles.errorText}>{formErrors.time}</p>}

            <h4 style={styles.label}>Where is your event located?</h4>
            <input placeholder="Enter location" style={styles.input} name="location" value={EventData.location} onChange={handleInputChange} />
            {formErrors.location && <p style={styles.errorText}>{formErrors.location}</p>}

            <h4 style={styles.label}>Tell us about your event!</h4>
            <textarea placeholder="Add additional details" style={styles.textarea} value={EventData.details} name="details" onChange={handleInputChange} />
          </div>

          <div style={styles.authForm}>
            <h2 style={styles.formHeading}>Log in or sign up to book</h2>
            <p style={styles.authDescription}>
              Sign up for our photobooth client portal to enjoy an easy booking
              process. View all your bookings and manage each event by easily
              viewing invoices, completing questionnaires, giving design
              feedback, and access to downloadable galleries. Make your
              photobooth experience unforgettable with just a few clicks!
            </p>
            <input placeholder="Email" style={styles.input} type="email" value={userData.email} name="email" onChange={handleUserFormInputChange} />
            
            {userExists === null && <button type="button" style={styles.button} onClick={handleCheckExistingUser}>Continue</button>}

            {userExists === true && (
              <>
                <div style={{ ...styles.passwordContainer, marginTop: "10px" }}>
                  <input ref={passwordInputRef} placeholder="Password" style={styles.input} type={passwordVisibility.login ? "text" : "password"} value={userData.password} name="password" onChange={handleUserFormInputChange} />
                  <span style={styles.passwordIcon} onClick={() => togglePasswordVisibility("login")}>
                    {passwordVisibility.login ? <FaEyeSlash /> : <FaEye />}
                  </span>
                </div>
                <button type="button" style={styles.button} onClick={handleLogin}>Continue</button>
              </>
            )}

{userExists === false && (
  <>
    <input 
      placeholder="Name" 
      style={{ ...styles.input, marginTop: "10px" }} 
      type="text" 
      value={userData.name} 
      name="name" 
      onChange={handleUserFormInputChange} 
    />
    {formErrors.name && <p style={styles.errorText}>{formErrors.name}</p>}

    {/* --- NEW PHONE NUMBER FIELD --- */}
    <input 
      placeholder="Phone Number" 
      style={{ ...styles.input, marginTop: "10px" }} 
      type="text" 
      value={userData.phone} 
      name="phone" 
      onChange={handleUserFormInputChange} 
    />
    {formErrors.phone && <p style={styles.errorText}>{formErrors.phone}</p>}
    
    <div style={{ ...styles.passwordContainer, marginTop: "10px" }}>
      <input 
        placeholder="Password" 
        style={styles.input} 
        type={passwordVisibility.new ? "text" : "password"} 
        value={userData.password} 
        name="password" 
        onChange={handleUserFormInputChange} 
      />
      <span style={styles.passwordIcon} onClick={() => togglePasswordVisibility("new")}>
        {passwordVisibility.new ? <FaEyeSlash /> : <FaEye />}
      </span>
    </div>
    {formErrors.password && <p style={styles.errorText}>{formErrors.password}</p>}
    
    <div style={{ ...styles.passwordContainer, marginTop: "10px" }}>
      <input 
        placeholder="Confirm Password" 
        style={styles.input} 
        type={passwordVisibility.confirm ? "text" : "password"} 
        value={userData.confirmPassword} 
        name="confirmPassword" 
        onChange={handleUserFormInputChange} 
      />
      <span style={styles.passwordIcon} onClick={() => togglePasswordVisibility("confirm")}>
        {passwordVisibility.confirm ? <FaEyeSlash /> : <FaEye />}
      </span>
    </div>
    {formErrors.confirmPassword && <p style={styles.errorText}>{formErrors.confirmPassword}</p>}
    
    <button style={styles.button} onClick={handleCreateAccountAndEvent}>Create Account</button>
  </>
)}
          </div>
        </div>
      </div>
    </div>
  );
};

const styles = {
  bookAbooth: { display: "flex", justifyContent: "center", padding: "20px", backgroundColor: "#F2F2F2", marginTop: "60px" },
  panel: { background: "#f2f2f2", width: "43%" },
  invoiceContent: { width: "100%", background: "#FFFFFF", padding: "20px", borderRadius: "8px", boxSizing: "border-box" },
  heading: { color: "#000000", margin: "0 0 20px 0" },
  formHeading: { color: "#000000", fontWeight: "600", marginTop: "0px" },
  package: { cursor: "pointer", marginTop: "15px", padding: "20px", border: "1px solid #D9D9D9", borderRadius: "8px", backgroundColor: "#FFFFFF" },
  packageHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" },
  packageName: { color: "#000000", margin: "0" },
  packageDescription: { color: "##625C73" },
  addon: { display: "flex", alignItems: "center", border: "1px solid #D9D9D9", borderRadius: "8px", backgroundColor: "#FFFFFF", padding: "10px", marginBottom: "10px", cursor: "pointer" },
  addonContent: { flex: "1" },
  addonName: { color: "#000000", margin: "0 0 5px 0" },
  addonDescription: { color: "#625C73", margin: "0" },
  label: { color: "#080700", marginTop: "15px", marginBottom: "10px", fontWeight: "500", width: '102%' },
  input: { width: "102%", height: "50px", border: "1px solid #D9D9D9", borderRadius: "5px", fontSize: "15px", fontFamily: "poppins", paddingLeft: "10px", paddingRight: "10px", boxSizing: "border-box", color: "#625C73" },
  textarea: { width: "102%", height: "100px", border: "1px solid #D9D9D9", borderRadius: "5px", fontSize: "15px", fontFamily: "poppins", padding: "10px", boxSizing: "border-box", color: "#625C73" },
  eventForm: { backgroundColor: "#FFFFFF", padding: "30px", borderRadius: "8px", marginTop: "35px" },
  authForm: { backgroundColor: "#ffffff", marginTop: "35px", borderRadius: "8px", padding: "30px" },
  authDescription: { color: "#625C73", fontSize: "15px", lineHeight: "1.6" },
  button: { marginTop: "15px", height: "45px", width: "102%", background: "linear-gradient(to right, #1C1154, #211074, #260F99)", border: "none", borderRadius: "4px", color: "#FFFFFF", fontSize: "17px", fontWeight: "500", cursor: "pointer" },
  errorText: { color: "red", fontSize: "12px", marginTop: "5px", marginBottom: "0px" },
  passwordContainer: { position: "relative", width: "100%" },
  passwordIcon: { position: "absolute", top: "50%", right: "15px", transform: "translateY(-50%)", cursor: "pointer", color: "#6c757d" },
};

export default BookABoothLink;